import {
  AdminCustomProviderSelect,
  AdminDatePickerInputModal,
  AdminInput,
  AdminSelect,
  Button,
  capitalize,
  DeliveryAssignmentNonOperatingConditionsEntity,
  Div,
  ErrorToast,
  format24hToDate,
  formatDateToTime24h,
  handleChangeNumInputTwoDecimalsMinMax,
  ModalPortal,
  P,
  Provider,
  WeekDay,
} from '@vgrubs/components';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

const days = Object.values(WeekDay);

const dayOptions = (
  <optgroup label="Select Day">
    {days.map((d, i) => (
      <option value={d} key={i}>
        {capitalize(d)}
      </option>
    ))}
  </optgroup>
);

export const AddNonOperating = ({
  isOpen,
  setIsOpen,
  setProviders,
  edit,
}: {
  isOpen:
    | boolean
    | { item: DeliveryAssignmentNonOperatingConditionsEntity; index: number };
  setIsOpen: Dispatch<
    SetStateAction<
      | boolean
      | { item: DeliveryAssignmentNonOperatingConditionsEntity; index: number }
    >
  >;
  setProviders: Dispatch<
    SetStateAction<DeliveryAssignmentNonOperatingConditionsEntity[]>
  >;
  edit?: boolean;
}) => {
  const [provider, setProvider] = useState<string | Provider | null>('');

  const [day, setDay] = useState(days[0]);
  const [startTime, setStartTime] = useState<Date[]>([]);
  const [endTime, setEndTime] = useState<Date[]>([]);
  const [distanceMiles, setDistanceMiles] = useState('');

  function toggleModal() {
    setIsOpen((prev) => !prev);
    clearState();
  }

  useEffect(() => {
    if (edit && isOpen && typeof isOpen === 'object') {
      setDay(isOpen.item.day);
      setStartTime(
        isOpen.item.startTime ? [format24hToDate(isOpen.item.startTime)] : []
      );
      setEndTime(
        isOpen.item.endTime ? [format24hToDate(isOpen.item.endTime)] : []
      );
      setDistanceMiles(isOpen.item.distanceMiles?.toString() || '');
      setProvider(isOpen.item.orderProvider || '');
    }
  }, [isOpen]);

  const clearState = () => {
    setDay(days[0]);
    setProvider('');
    setStartTime([]);
    setEndTime([]);
    setDistanceMiles('');
  };

  const handleSubmit = () => {
    if (!day) {
      return ErrorToast('Please Select a Day.');
    }

    const hasStartOrEnd = startTime || endTime;
    const hasDistance = !!distanceMiles;
    const hasOrderProvider = !!provider;

    if (!hasStartOrEnd && !hasDistance && !hasOrderProvider) {
      return ErrorToast(
        'Please provide at least one of: Distance, Time range or Order Provider.'
      );
    }

    if (hasStartOrEnd) {
      if ((startTime && !endTime) || (!startTime && endTime)) {
        return ErrorToast('Start Time and End Time must be provided together.');
      }
      if (startTime && endTime && startTime[0] >= endTime[0])
        return ErrorToast('Time From must be earlier than Time To');
    }

    const objectToAdd = {
      day,
      startTime: startTime[0] ? formatDateToTime24h(startTime[0]) : null,
      endTime: endTime[0] ? formatDateToTime24h(endTime[0]) : null,
      distanceMiles: Number(distanceMiles) || null,
      orderProvider: provider || null,
    };

    setProviders((prev) => [
      ...prev,
      objectToAdd as DeliveryAssignmentNonOperatingConditionsEntity,
    ]);
    toggleModal();
  };

  const handleSubmitEdit = () => {
    const objectToEdit = {
      day,
      startTime: startTime[0] ? formatDateToTime24h(startTime[0]) : null,
      endTime: endTime[0] ? formatDateToTime24h(endTime[0]) : null,
      distanceMiles: Number(distanceMiles) || null,
      orderProvider: provider || null,
    };

    setProviders((prev) =>
      prev.map((p, i) =>
        i ===
        (
          isOpen as {
            item: DeliveryAssignmentNonOperatingConditionsEntity;
            index: number;
          }
        ).index
          ? ({
              ...p,
              ...objectToEdit,
            } as DeliveryAssignmentNonOperatingConditionsEntity)
          : (p as DeliveryAssignmentNonOperatingConditionsEntity)
      )
    );
    toggleModal();
  };

  return (
    <ModalPortal
      header={edit ? 'Edit Non-Operating Day' : 'Add Non-Operating Day'}
      isOpen={!!isOpen}
      closeModal={toggleModal}
      overflow="visible"
      zIndex={131}
    >
      <Div $flex $column $gap="24px">
        <AdminSelect
          label="Day"
          options={dayOptions}
          value={day}
          onChange={({ target }: { target: HTMLSelectElement }) =>
            setDay(target.value as WeekDay)
          }
          placeholder="From"
          $textCenter
        />

        <div>
          <P $bold $mb="8" $center>
            Select Hours From - To
          </P>
          <Div $flex $gap="24px">
            <AdminDatePickerInputModal
              format="hh:mm a"
              options={{
                enableTime: true,
                dateFormat: 'Y-m-d H:i',
                noCalendar: true,
              }}
              placeholder="From"
              $textCenter
              onChange={setStartTime}
              value={startTime}
            />
            <AdminDatePickerInputModal
              format="hh:mm a"
              options={{
                enableTime: true,
                dateFormat: 'Y-m-d H:i',
                noCalendar: true,
              }}
              placeholder="To"
              $textCenter
              onChange={setEndTime}
              value={endTime}
            />
          </Div>
        </div>
        <AdminCustomProviderSelect
          value={provider}
          onChange={(value) => {
            setProvider(value as string);
          }}
          label="Order Provider"
          $textCenter
          $shadowDesign={false}
        />
        <AdminInput
          label="Distance in Miles"
          $textCenter
          value={distanceMiles}
          placeholder="Enter Here"
          onChange={(e) =>
            handleChangeNumInputTwoDecimalsMinMax(
              e,
              setDistanceMiles,
              0,
              1000000
            )
          }
        />
      </Div>
      <Button
        $w="100%"
        $mt="32"
        $selected
        onClick={edit ? handleSubmitEdit : handleSubmit}
      >
        Confirm
      </Button>
      <Button $w="100%" $mt="16" $selected $gray onClick={toggleModal}>
        Cancel
      </Button>
    </ModalPortal>
  );
};
